import {SxProps, Theme} from "@mui/material";

export const getFieldSx = (name: string, error: string | undefined, size?: string): SxProps<Theme> => {
    const hasError = !!error;
    const focusedColor = '#52ae30';
    const errorColor = '#ef4444';
    const defaultBorderColor = '#9C9FA3';
    const hoverBorderColor = '#2E2F30';

    const smFields = ['landlineAreaCode', 'mobileAreaCode', 'houseNumber', 'secondHouseNumber', 'postalCode', 'secondPostalCode'];
    const mdFields = ['landlineNumber', 'mobileNumber', 'street', 'secondStreet', 'placeOfResidence', 'secondPlaceOfResidence'];
    const multilineFields = ['izvorSredstava', 'izvorSredstavaBanka'];

    let fieldWidth = '100%';
    if (smFields.includes(name) && size !== "sm") {
        fieldWidth = '40%';
    } else if (mdFields.includes(name) && size !== "sm") {
        fieldWidth = '58%';
    }

    let borderRadius = '24px';
    if (multilineFields.includes(name)) {
        borderRadius = '50px';
    }

    return {
        width: fieldWidth,
        '& .MuiOutlinedInput-root': {
            borderRadius: borderRadius,
            backgroundColor: 'white',
            transition: (theme) => theme.transitions.create(['border-color', 'box-shadow']),
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: hasError ? errorColor : defaultBorderColor,
                borderWidth: hasError ? '2px' : '1px',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: hasError ? errorColor : hoverBorderColor,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: hasError ? errorColor : focusedColor,
                borderWidth: '2px',
            },
            '&.Mui-disabled': {
                backgroundColor: 'white',
                '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: defaultBorderColor,
                },
            },
        },
        '& .MuiOutlinedInput-input': {
            color: 'black',
            '&.Mui-disabled': {
                color: 'black',
                WebkitTextFillColor: 'black',
            },
        },
        '& .MuiAutocomplete-inputRoot .MuiOutlinedInput-input': {
            color: 'black',
            '&.Mui-disabled': {
                color: 'black',
                WebkitTextFillColor: 'black',
            },
        },
        '& .MuiInputLabel-root': {
            '&.Mui-focused': {
                color: hasError ? errorColor : focusedColor,
            },
            '&.Mui-disabled': {
                color: 'black',
            },
        },
        '& .MuiFormHelperText-root': {
            marginLeft: '14px',
            marginRight: '14px',
            '&.Mui-error': {
                color: errorColor,
            },
        },
    };
};

export const getSwitchSx = (switched: boolean): SxProps<Theme> => {
    return {
        '& .MuiSwitch-thumb':
          {
              bgcolor: switched ? "#52ae30 !important" : undefined
          }
        ,
        '& .MuiSwitch-track':
          {
              bgcolor: switched ? "#cbedbf !important" : undefined
          }
        ,
        '& .MuiSwitch-switchBase.Mui-checked':
          {
              color: "#52ae30",
              '& + .MuiSwitch-track':
                {
                    backgroundColor: "#cbedbf",
                    opacity:
                      1,
                }
              ,
              '&:hover':
                {
                    bgcolor: "rgba(82, 174, 48, 0.08)"
                },
          }
        ,
        '& .MuiSwitch-switchBase':
          {
              color: '#bdbdbd',
              '&:hover':
                {
                    bgcolor: "rgba(0, 0, 0, 0.04)"
                },
          }
    }
};

export const getDatePickerFieldSx = (name: string, error: string | undefined): SxProps<Theme> => {
    const hasError = !!error;
    const focusedColor = '#52ae30';
    const errorColor = '#ef4444';
    const defaultBorderColor = '#9C9FA3';
    const hoverBorderColor = '#2E2F30';
    const fieldWidth = '100%';
    const borderRadius = '24px';
    return {
        width: fieldWidth,
        '& .MuiOutlinedInput-root': { borderRadius, backgroundColor: 'white', transition: (theme) => theme.transitions.create(['border-color', 'box-shadow']), '& .MuiOutlinedInput-notchedOutline': { borderColor: hasError ? errorColor : defaultBorderColor, borderWidth: hasError ? '2px' : '1px' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: hasError ? errorColor : hoverBorderColor }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: hasError ? errorColor : focusedColor, borderWidth: '2px' }, '&.Mui-disabled': { backgroundColor: 'white', '& .MuiOutlinedInput-notchedOutline': { borderColor: defaultBorderColor } } },
        '& .MuiOutlinedInput-input': { color: 'black', padding: '16.5px 14px', '&.Mui-disabled': { color: 'black', WebkitTextFillColor: 'black' } },
        '& .MuiInputLabel-root': { '&.Mui-focused': { color: hasError ? errorColor : focusedColor }, '&.Mui-disabled': { color: 'black' } },
        '& .MuiFormHelperText-root': { marginLeft: '14px', marginRight: '14px', '&.Mui-error': { color: errorColor } },
    };
};

export const getAutocompleteSx = (name: string, error: string | undefined, size?: string): SxProps<Theme> => {
    const hasError = !!error;
    const focusedColor = '#52ae30';
    const errorColor = '#ef4444';
    const defaultBorderColor = '#9C9FA3';
    const hoverBorderColor = '#2E2F30';
    const disabledTextColor = '#9C9FA3';

    const smFields = ['landlineAreaCode', 'mobileAreaCode', 'houseNumber', 'secondHouseNumber', 'postalCode', 'secondPostalCode'];
    const mdFields = ['landlineNumber', 'mobileNumber', 'street', 'secondStreet', 'placeOfResidence', 'secondPlaceOfResidence'];
    const multilineFields = ['izvorSredstava', 'izvorSredstavaBanka'];

    let fieldWidth = '100%';
    if (smFields.includes(name) && size !== "sm") {
        fieldWidth = '40%';
    } else if (mdFields.includes(name) && size !== "sm") {
        fieldWidth = '58%';
    }

    let borderRadius = '24px';
    if (multilineFields.includes(name)) {
        borderRadius = '50px';
    }

    return {
        width: fieldWidth,
        '& .MuiOutlinedInput-root': {
            borderRadius: borderRadius,
            backgroundColor: 'white',
            transition: (theme) => theme.transitions.create(['border-color', 'box-shadow', 'background-color']),
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: hasError ? errorColor : defaultBorderColor,
                borderWidth: hasError ? '2px' : '1px',
            },
            '&:hover:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline': {
                borderColor: hasError ? errorColor : hoverBorderColor,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: hasError ? errorColor : focusedColor,
                borderWidth: '2px',
            },
        },
        '& .MuiOutlinedInput-input': {
            color: 'black',
            '&.Mui-disabled': {
                color: disabledTextColor,
                WebkitTextFillColor: disabledTextColor,
            },
        },
        '& .MuiInputLabel-root': {
            '&.Mui-focused': {
                color: hasError ? errorColor : focusedColor,
            },
            '&.Mui-disabled': {
                color: disabledTextColor,
            },
        },
        '& .MuiFormHelperText-root': {
            marginLeft: '14px',
            marginRight: '14px',
            '&.Mui-error': {
                color: errorColor,
            },
        },
    };
};


